import React, { useEffect, useState } from "react";
import { Form } from "react-bootstrap";
import SupplierSearch from "@components/SupplierSearch";
import { LocationSearch } from "@components/Select";
import { Region } from "@api/getregions";
import { SelectedSupplier } from "@components/Supplier/AddSupplier";
import scss from "./BookedVenue.module.scss";
import useUser from "@hooks/useUser";

const VALUE_YES = "Yes";
const VALUE_NO = "No";
const RADIO_NAME = "wedding-booked";

export interface IBookedResult {
    venueSupplierId?: number;
    venueSupplierName?: string;
    regionId?: number;
    regionName?: string;
}

interface IBookedVenueProps extends IBookedResult {
    onChange: (result: IBookedResult) => void;
}

const BookedVenue = ({ venueSupplierId, venueSupplierName, regionId, onChange }: IBookedVenueProps) => {
    const [booked, setBooked] = useState<string>(!!venueSupplierName ? VALUE_YES : '');
    const [currentRegion, setCurrentRegion] = useState<Region | null>(null);
    const { user } = useUser();

    useEffect(() => {
        if ((user && user.bookedVenueSupplierId) || !!venueSupplierId) {
            setBooked(VALUE_YES);
        }
        else if (!!regionId) {
            setBooked(VALUE_NO);
            setCurrentRegion({
                Id: regionId,
                Name: '',
                ShortName: '',
                Slug: ''
            });
        }
    }, [user, venueSupplierId, regionId]);

    const handleRadioChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setBooked(value);

        if (value === VALUE_NO) {
            // Clear venue-related fields when "No" is selected
            onChange({
                venueSupplierId: 0,
                venueSupplierName: '',
                regionId: currentRegion?.Id || 0,
                regionName: currentRegion?.Name || ''
            });
        }
    };

    const handleVenueChange = (supplier: SelectedSupplier | null) => {
        onChange({
            venueSupplierId: !!supplier ? Number(supplier.Id) : 0,
            venueSupplierName: !!supplier ? supplier.Name : '',
            regionId: !!currentRegion ? currentRegion.Id : 0,
            regionName: !!currentRegion ? currentRegion.Name : ''
        });
    };

    const handleRegionChange: React.Dispatch<React.SetStateAction<Region | null>> = (newRegion) => {
        if (newRegion && typeof newRegion !== 'function') {
            setCurrentRegion(newRegion);
            onChange({
                venueSupplierId,
                venueSupplierName,
                regionId: newRegion.Id,
                regionName: newRegion.Name
            });
        }
    };

    return (
        <>
            <div className="d-flex align-items-center">
                <Form.Label className={'ms-1 me-3 mt-2 small d-inline-block text-shade'}>Have you booked your wedding venue?</Form.Label>
                <div className="d-flex">
                    <Form.Check
                        inline
                        id={`${RADIO_NAME}-yes`}
                        label={VALUE_YES}
                        value={VALUE_YES}
                        name={RADIO_NAME}
                        type={'radio'}
                        className={scss.radio}
                        onChange={handleRadioChange}
                        checked={booked === VALUE_YES}
                        data-testid="venue-booked-yes"
                    />
                    <Form.Check
                        inline
                        id={`${RADIO_NAME}-no`}
                        label={VALUE_NO}
                        value={VALUE_NO}
                        name={RADIO_NAME}
                        type={'radio'}
                        className={scss.radio}
                        onChange={handleRadioChange}
                        checked={booked === VALUE_NO}
                        data-testid="venue-booked-no"
                    />
                </div>
            </div>
            {booked === VALUE_YES && <SupplierSearch
                categoryName={'Venue'}
                categorySlug={'WeddingVenues'}
                placeholder={'Name of the venue you\'ve booked'}
                isFocus={false}
                hideBackground={true}
                hideAfterSelect={true}
                callOnClear={true}
                defaultValue={venueSupplierName ?? user?.bookedVenueSupplierName ?? ''}
                formElementHeight={true}
                handleSaveSupplier={handleVenueChange}
            />}
            {booked === VALUE_NO && <LocationSearch
                customDropdownClass={'w-100'}
                initialRegionId={currentRegion?.Id}
                currentRegion={currentRegion}
                setCurrentRegion={handleRegionChange}
            />}
        </>
    );
};

export default BookedVenue;